import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comicDetails, allComics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);

  const comic = comicDetails[id || ''] || null;

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return (comic.relatedComics || [])
      .map((rid: string) => allComics.find((c) => c.id === rid))
      .filter(Boolean);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-950 mt-4">漫画未找到</h1>
            <p className="text-foreground-500 mt-2 text-sm">该漫画可能已下架或不存在</p>
            <Link to="/" className="inline-block mt-4 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 10);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: `${comic.category}漫画`, path: `/category/${categories.find(c => c.name === comic.category)?.slug || 'hanman'}` },
              { label: comic.title },
            ]} />

            <article className="mt-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-[280px] flex-shrink-0">
                  <div className="rounded-lg overflow-hidden bg-background-100">
                    <div className="aspect-[3/4]">
                      <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link
                      to="#chapters"
                      className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg text-center transition-colors cursor-pointer whitespace-nowrap"
                    >
                      开始阅读
                    </Link>
                    <button className="w-10 h-10 flex items-center justify-center border border-background-200 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                      <i className="ri-bookmark-line text-foreground-600"></i>
                    </button>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{comic.title}</h1>

                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-500">分类：{comic.category}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-primary-500' : 'text-secondary-500'}`}>
                      {comic.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-6 mt-4">
                    <div className="flex items-center gap-1.5">
                      <span className="font-heading text-2xl text-accent-500">{comic.rating}</span>
                      <span className="text-sm text-foreground-500">分</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{comic.views}</p>
                      <p className="text-xs text-foreground-400">总阅读量</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{comic.chapters.length}</p>
                      <p className="text-xs text-foreground-400">章节数</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {comic.tags.map((tag: string) => (
                      <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6">
                    <h3 className="font-heading text-base text-foreground-950 mb-2">作品简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="mt-8" id="chapters">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg text-foreground-950">章节目录</h3>
                      <span className="text-xs text-foreground-400">共 {comic.chapters.length} 话</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {displayedChapters.map((ch: any) => (
                        <div
                          key={ch.num}
                          className="flex items-center justify-between p-3 bg-background-50 border border-background-200/60 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <i className="ri-file-text-line text-foreground-400 text-sm flex-shrink-0"></i>
                            <span className="text-sm text-foreground-950 truncate">第{ch.num}话 {ch.title}</span>
                          </div>
                          <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.date}</span>
                        </div>
                      ))}
                    </div>
                    {comic.chapters.length > 10 && (
                      <button
                        onClick={() => setShowAllChapters(!showAllChapters)}
                        className="mt-3 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
                      >
                        {showAllChapters ? '收起目录' : `查看全部 ${comic.chapters.length} 话`}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {relatedComics.length > 0 && (
                <section className="mt-14">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                    <h2 className="font-heading text-xl text-foreground-950">相关推荐</h2>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-product-shop="">
                    {relatedComics.map((rc: any) => (
                      <ComicCard
                        key={rc.id}
                        id={rc.id}
                        title={rc.title}
                        cover={rc.cover}
                        category={rc.category}
                        rating={rc.rating}
                        status={rc.status}
                      />
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}